import { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Settings2 } from 'lucide-react';

export default function PlanAdvancedSettingsModal({
  open,
  onOpenChange,
  block,
  assessmentSets,
  onUpdated,
  onOpenAssessmentManager,
}) {
  const { t } = useLanguage();
  const [assessmentSetId, setAssessmentSetId] = useState('_default');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && block) {
      setAssessmentSetId(block.assessment_set_id ? String(block.assessment_set_id) : '_default');
    }
  }, [open, block?.id, block?.assessment_set_id]);

  const save = async () => {
    if (!block?.id) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/training/periodization/blocks/${block.id}`, {
        assessment_set_id: assessmentSetId === '_default' ? null : parseInt(assessmentSetId, 10),
      });
      onUpdated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.advanced.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>{t('periodization.advanced.title')}</DialogTitle>
          <DialogDescription>{t('periodization.advanced.description')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <div className='flex items-center justify-between gap-2'>
              <Label className='text-xs'>{t('periodization.assessmentSet')}</Label>
              <Button
                type='button'
                size='sm'
                variant='ghost'
                className='h-7 gap-1 text-xs'
                onClick={() => {
                  onOpenChange(false);
                  onOpenAssessmentManager?.();
                }}
              >
                <Settings2 className='h-3.5 w-3.5' />
                {t('periodization.manageAssessment')}
              </Button>
            </div>
            <Select value={assessmentSetId} onValueChange={setAssessmentSetId}>
              <SelectTrigger className='h-9 text-xs'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='_default'>{t('periodization.defaultAssessmentSet')}</SelectItem>
                {(assessmentSets || []).map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                    {s.scope === 'global' ? ` (${t('periodization.assessmentManager.global')})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-[10px] text-muted-foreground'>{t('periodization.advanced.assessmentSetHint')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type='button' onClick={save} disabled={saving}>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
